import React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import styled from '@emotion/styled'
import { mq } from '../../../helpers'
import { faqAnswerToHtml, toJsonLdString } from '../../../helpers/faq'
import FaqAccordion from '../../../components/FAQ/FaqAccordion'
import FaqPager from '../../../components/FAQ/FaqPager'
import logger from '../../../services/logger'
import {
  FAQ_PAGE_SIZE,
  fetchAllCategories,
  fetchCategoryBySlug,
  fetchCategoryFaqsPage,
} from '../../../services/faqStrapi'

export async function getStaticPaths() {
  try {
    const categories = await fetchAllCategories()
    const paths = categories.map((category) => ({
      params: { slug: category.slug || category.documentId },
    }))
    return { paths, fallback: 'blocking' }
  } catch (error) {
    logger.error('FAQ Category.getStaticPaths: error fetching categories', { error })
    return { paths: [], fallback: 'blocking' }
  }
}

export async function getStaticProps(context) {
  const { slug } = context.params
  try {
    const category = await fetchCategoryBySlug(slug)
    if (!category) { return { notFound: true, revalidate: 60 } }

    const categoryId = category.documentId || category.id
    const { faqs, pagination } = await fetchCategoryFaqsPage(categoryId, 1, FAQ_PAGE_SIZE)

    return {
      props: {
        category,
        faqs,
        currentPage: 1,
        pageCount: pagination.pageCount || 1,
      },
      revalidate: 60,
    }
  } catch (error) {
    logger.error('FAQ Category.getStaticProps: error', { error, slug })
    return { notFound: true, revalidate: 60 }
  }
}

export default function CategoryPage({ category, faqs, currentPage, pageCount }) {
  const categoryName = category.name || category.title
  const categorySlug = category.slug || category.documentId

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question || faq.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faqAnswerToHtml(faq.answer ?? faq.body).replace(/<[^>]*>/g, ''),
      },
    })),
  }

  return (
    <div>
      <Head>
        <title>{categoryName} - FAQs - Glamsquad</title>
        <meta name="title" content={`${categoryName} - FAQs - Glamsquad`} />
        <meta name="description" content={`Frequently asked questions about ${categoryName}`} />
        <link rel="canonical" href={`https://glamsquad.com/faq/category/${categorySlug}`} />
        {pageCount > 1 && (
          <link rel="next" href={`https://glamsquad.com/faq/category/${categorySlug}/page/2`} />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: toJsonLdString(jsonLd) }}
        />
      </Head>
      <CategoryPageWrapper>
        <div className='back-link'>
          <Link href="/faq">← Back to all FAQs</Link>
        </div>
        <div className='header-container'>
          <h1>{categoryName}</h1>
        </div>
        <div className='faq-list'>
          <FaqAccordion faqs={faqs} />
          <FaqPager categorySlug={categorySlug} currentPage={currentPage} pageCount={pageCount} />
        </div>
      </CategoryPageWrapper>
    </div>
  )
}

const CategoryPageWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;

  .back-link {
    padding: 40px 88px 0 88px;

    a {
      color: #666;
      text-decoration: none;
      font-family: Euclid;
      font-size: 16px;

      &:hover {
        color: #000;
      }
    }

    ${mq('max', '800px')} {
      padding: 20px 16px 0 16px;
    }
  }

  .header-container {
    padding: 60px 88px 24px 88px;

    ${mq('max', '800px')} {
      padding: 30px 16px 12px 16px;
    }

    h1 {
      color: var(--Black, #000);
      font-family: Garnett;
      font-size: 48px;
      font-weight: 750;
      line-height: 130%;
      margin: 0;

      ${mq('max', '800px')} {
        font-size: 32px;
      }
    }
  }

  .faq-list {
    padding: 0 88px 80px 88px;

    ${mq('max', '800px')} {
      padding: 0 16px 40px 16px;
    }
  }
`
